import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Order } from '../models/Order';
import type { OrderStatus } from '../models/enums';
import { orderService } from '../services/dataverse/orderService';

export const useOrders = () => {
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<OrderStatus | 'all'>('all');

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await orderService.list();
      setAllOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadOrders(); }, [loadOrders]);

  // Client-side filtering — no API call on every keystroke
  const orders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allOrders
      .filter((o) => status === 'all' || o.cr_crm_status === status)
      .filter((o) => !term || [
        o.cr_crm_orderreference,
        o.cr_crm_client?.cr_crm_companyname,
        o.cr_crm_partnercenterorderid,
      ].filter(Boolean).some((v) => v?.toLowerCase().includes(term)));
  }, [allOrders, search, status]);

  return { orders, loading, error, search, setSearch, status, setStatus, reload: loadOrders };
};
